import React, { useState, useEffect, useRef } from 'react';
import Navigation from '../components/Navigation';
import { useAuth } from '../context/AuthContext';
import useVetAppointments from '../hooks/useVetAppointments';
import VetBookingWizard from '../components/VetBookingWizard';
import WalkInVetForm from '../components/WalkInVetForm';
import {
    APPOINTMENT_STATUS,
    STATUS_LABELS,
    formatDate,
    formatDateTime,
    formatDateForInput
} from '../utils/vetUtils';
import { VetService } from '../services/vetService';
import '../styles/pages/vet.css';

const Vet = () => {
    // Hooks
    const {
        appointments,
        loading,
        error,
        filters,
        createAppointment,
        createPuppySchema,
        createAdultSchema,
        createWalkInAppointment,
        updateAppointment,
        updateWalkInAppointment,
        updateAppointmentStatus,
        deleteAppointment,
        updateFilters,
        clearFilters,
        clearError
    } = useVetAppointments();
    const { authorizedFetch } = useAuth();
    
    // useDogs is used inside VetBookingWizard — not needed here directly

    // Local state
    const [activeTab, setActiveTab] = useState('list');
    const [viewMode, setViewMode] = useState('active');
    const [editingAppointment, setEditingAppointment] = useState(null);
    const [editingWalkIn, setEditingWalkIn] = useState(null);
    const [conductingExam, setConductingExam] = useState(null); // For exam mode
    const [message, setMessage] = useState({ type: '', text: '' });
    // Exam form data (during examination)
    const [examFormData, setExamFormData] = useState({
        temperature: '',
        weight: '',
        findings: '',
        recommendations: '',
        vetNotes: '',
        followUpRequired: false,
        followUpDate: '',
    });
    
    const [formErrors, setFormErrors] = useState({});
    const [collapsedSections, setCollapsedSections] = useState({ earlier: true });
    // Attachments for the current exam
    const [attachments, setAttachments] = useState([]);
    const [attachUploading, setAttachUploading] = useState(false);
    const fileInputRef = useRef(null);

    // Clear messages after 5 seconds
    useEffect(() => {
        if (message.text) {
            const timer = setTimeout(() => {
                setMessage({ type: '', text: '' });
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    // Reset booking form
    const resetBookingForm = () => {
        setEditingAppointment(null);
        setFormErrors({});
    };
    
    // Reset exam form
    const resetExamForm = () => {
        setExamFormData({
            temperature: '',
            weight: '',
            findings: '',
            recommendations: '',
            vetNotes: '',
            followUpRequired: false,
            followUpDate: '',
        });
        setFormErrors({});
        setConductingExam(null);
    };

    // Handle exam form input changes
    const handleExamInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setExamFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Clear error for this field
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        updateFilters({ [name]: value });
    };

    // Handle booking form submission from VetBookingWizard
    const handleBookingFormSubmit = async (formData) => {
        clearError();
        try {
            if (formData.isSchema) {
                const created = await createPuppySchema({ dogId: formData.dogId, firstDate: formData.firstDate });
                setMessage({ type: 'success', text: `¡Esquema cachorro creado! Se programaron ${created.length} citas.` });
                resetBookingForm();
                setActiveTab('list');
                return;
            }
            if (formData.isAdultSchema) {
                const created = await createAdultSchema({ dogId: formData.dogId, firstDate: formData.firstDate, vaccines: formData.vaccines });
                setMessage({ type: 'success', text: `¡Esquema adulto creado! Próxima aplicación en un año.` });
                resetBookingForm();
                setActiveTab('list');
                return;
            }
            if (editingAppointment) {
                await updateAppointment(editingAppointment._id, formData);
                setMessage({ type: 'success', text: '¡Cita actualizada con éxito!' });
            } else {
                await createAppointment(formData);
                setMessage({ type: 'success', text: '¡Cita programada con éxito!' });
            }
            resetBookingForm();
            setActiveTab('list');
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };
    
    // Handle walk-in form submission
    const handleWalkInSubmit = async (formData) => {
        clearError();
        try {
            const booked = formData.status === APPOINTMENT_STATUS.SCHEDULED;
            if (editingWalkIn) {
                await updateWalkInAppointment(editingWalkIn._id, formData);
                setMessage({ type: 'success', text: '¡Visita walk-in actualizada!' });
            } else {
                await createWalkInAppointment(formData);
                setMessage({
                    type: 'success',
                    text: booked
                        ? `¡Visita programada para el ${formatDateTime(formData.appointmentDate)}!`
                        : '¡Visita walk-in registrada!',
                });
            }
            setEditingWalkIn(null);
            setActiveTab('list');
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    // Handle exam form submission (update with medical details)
    const handleExamSubmit = async (finalStatus) => {
        setFormErrors({});
        clearError();
        if (!conductingExam) return;
        try {
            await updateAppointment(conductingExam._id, {
                temperature: examFormData.temperature,
                weight: examFormData.weight,
                findings: examFormData.findings,
                recommendations: examFormData.recommendations,
                vetNotes: examFormData.vetNotes,
                followUpRequired: examFormData.followUpRequired,
                followUpDate: examFormData.followUpDate,
                status: finalStatus,
            }, true);
            setMessage({
                type: 'success',
                text: finalStatus === APPOINTMENT_STATUS.COMPLETED
                    ? '¡Examen completado!'
                    : 'Borrador guardado.',
            });
            resetExamForm();
            setActiveTab('list');
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    // Handle edit appointment
    const handleEdit = (appointment) => {
        if (appointment.isWalkIn) {
            setEditingWalkIn(appointment);
            setActiveTab('walkin-form');
        } else {
            setEditingAppointment(appointment);
            setActiveTab('booking-form');
        }
    };
    
    // Handle start examination (medical details)
    const handleStartExam = (appointment) => {
        setExamFormData({
            temperature: appointment.temperature || '',
            weight: appointment.weight || '',
            findings: appointment.findings || '',
            recommendations: appointment.recommendations || '',
            vetNotes: appointment.vetNotes || '',
            followUpRequired: appointment.followUpRequired || false,
            followUpDate: appointment.followUpDate ? formatDateForInput(appointment.followUpDate) : '',
        });
        setAttachments(appointment.attachments || []);
        setConductingExam(appointment);
        setActiveTab('exam-form');
    };

    const handleAttachFiles = async (files) => {
        if (!conductingExam || !files?.length) return;
        setAttachUploading(true);
        try {
            for (const file of Array.from(files)) {
                const att = await VetService.uploadAttachment(conductingExam._id, file, authorizedFetch);
                setAttachments(prev => [...prev, att]);
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setAttachUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDeleteAttachment = async (attachmentId) => {
        if (!conductingExam) return;
        try {
            await VetService.deleteAttachment(conductingExam._id, attachmentId, authorizedFetch);
            setAttachments(prev => prev.filter(a => a._id !== attachmentId));
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    // Handle status update
    const handleStatusUpdate = async (appointmentId, newStatus) => {
        try {
            await updateAppointmentStatus(appointmentId, newStatus);
            setMessage({ type: 'success', text: '¡Estado de la cita actualizado con éxito!' });
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    // Handle delete appointment
    const handleDelete = async (appointmentId) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar esta cita?')) {
            try {
                await deleteAppointment(appointmentId);
                setMessage({ type: 'success', text: '¡Cita eliminada con éxito!' });
            } catch (err) {
                setMessage({ type: 'error', text: err.message });
            }
        }
    };

    // Render appointment card
    const renderAppointmentCard = (appointment) => {
        const isWalkIn = appointment.isWalkIn;
        const dog = appointment.dog;
        const displayName  = isWalkIn ? appointment.walkInInfo?.dogName : (dog?.name || 'Unknown Dog');
        const displayOwner = isWalkIn ? appointment.walkInInfo?.fullName : (dog?.owners?.map(o => o.name).join(', ') || '—');
        const isHistory = appointment.status === APPOINTMENT_STATUS.COMPLETED || appointment.status === APPOINTMENT_STATUS.CANCELLED;
        const hasExam = appointment.temperature || appointment.weight || appointment.findings || appointment.recommendations || appointment.vetNotes;

        return (
            <div key={appointment._id} className={`vt-card${isHistory ? ' vt-card--history' : ''}`}>
                <div className="vt-cc-top">
                    <div className="vt-cc-avatar">
                        {!isWalkIn && (dog?.pictureUrl || dog?.picture)
                            ? <img src={dog.pictureUrl || dog.picture} alt={dog.name} />
                            : <span>{displayName?.[0]?.toUpperCase() || '🐾'}</span>
                        }
                    </div>
                    <div className="vt-cc-info">
                        <div className="vt-cc-name">
                            {displayName}
                            {isWalkIn && <span className="vt-walkin-badge">Walk-in</span>}
                        </div>
                        <div className="vt-cc-meta">
                            {displayOwner && displayOwner !== '—' && <span>{displayOwner}</span>}
                            {displayOwner && displayOwner !== '—' && <span className="vt-cc-sep">·</span>}
                            <span>{formatDateTime(appointment.appointmentDate)}</span>
                            {isWalkIn && appointment.walkInInfo?.phone && (
                                <><span className="vt-cc-sep">·</span><span>{appointment.walkInInfo.phone}</span></>
                            )}
                        </div>
                    </div>
                    <span className={`vt-status-pill vt-status-${appointment.status}`}>
                        {STATUS_LABELS[appointment.status]}
                    </span>
                </div>

                {appointment.schemaId && (
                    <div className="vt-cc-schema">
                        <span className="vt-cc-schema-label">
                            {appointment.cause === 'Esquema cachorro' ? '🐶' : '🐕'} {appointment.cause}
                        </span>
                        <span className="vt-cc-schema-step">Paso {appointment.schemaStep}/{appointment.schemaTotal}</span>
                        <div className="vt-cc-schema-dots">
                            {Array.from({ length: appointment.schemaTotal }, (_, i) => (
                                <span key={i} className={`vt-cc-dot${i < appointment.schemaStep ? ' active' : ''}`} />
                            ))}
                        </div>
                    </div>
                )}

                <div className="vt-cc-tags">
                    <span className="vt-cc-cause">🩺 {appointment.cause}</span>
                    {appointment.temperature && <span className="vt-cc-vital">🌡️ {appointment.temperature}°C</span>}
                    {appointment.weight && <span className="vt-cc-vital">⚖️ {appointment.weight}kg</span>}
                    {appointment.vaccines?.map(v => (
                        <span key={v} className="vt-cc-vaccine">💉 {v}</span>
                    ))}
                    {appointment.followUpRequired && (
                        <span className="vt-cc-followup-tag">
                            🔔 {appointment.followUpDate ? formatDate(appointment.followUpDate) : 'Seguimiento'}
                        </span>
                    )}
                </div>

                <div className="vt-card-actions">
                    <button onClick={() => handleEdit(appointment)} className="vt-btn vt-btn-edit">Editar</button>
                    {!isHistory && (
                        <button onClick={() => handleStartExam(appointment)} className="vt-btn vt-btn-exam">
                            {appointment.status === APPOINTMENT_STATUS.SCHEDULED ? 'Iniciar examen' : 'Continuar examen'}
                        </button>
                    )}
                    {isHistory && hasExam && (
                        <button onClick={() => handleStartExam(appointment)} className="vt-btn vt-btn-view">Editar examen</button>
                    )}
                    <button onClick={() => handleDelete(appointment._id)} className="vt-btn vt-btn-delete">Eliminar</button>
                </div>
            </div>
        );
    };

    return (
        <div>
            <Navigation />
            <div className="vet-container">
                <div className="vt-page-header">
                    <h1 className="vt-page-title">Veterinaria</h1>
                    <div className="vt-header-actions">
                        <button
                            className="vt-new-btn vt-walkin-btn"
                            onClick={() => { setEditingWalkIn(null); setActiveTab('walkin-form'); }}
                        >
                            🚶 Walk-in
                        </button>
                        <button
                            className="vt-new-btn"
                            onClick={() => {
                                if (!editingAppointment) resetBookingForm();
                                setActiveTab('booking-form');
                            }}
                        >
                            + Nueva cita
                        </button>
                    </div>
                </div>

                {/* Messages */}
                {(message.text || error) && (
                    <div className={`message ${message.type || 'error'}`}>
                        {message.text || error}
                    </div>
                )}

                {/* Tab nav — only shown when an exam is in progress */}
                {conductingExam && (
                    <div className="tab-navigation">
                        <button
                            onClick={() => { setActiveTab('list'); resetBookingForm(); resetExamForm(); }}
                            className={`tab-button ${activeTab === 'list' ? 'active' : ''}`}
                        >
                            Citas
                        </button>
                        <button
                            onClick={() => setActiveTab('exam-form')}
                            className={`tab-button ${activeTab === 'exam-form' ? 'active' : ''}`}
                        >
                            Examen — {conductingExam.dog?.name || conductingExam.walkInInfo?.dogName || 'Perro desconocido'}
                        </button>
                    </div>
                )}

                {/* Appointments List Tab */}
                {activeTab === 'list' && viewMode === 'active' && (
                    <div className="vt-page">
                        {/* View mode tabs */}
                        <div className="vt-view-tabs">
                            <button
                                className={`vt-view-tab${viewMode === 'active' ? ' active' : ''}`}
                                onClick={() => setViewMode('active')}
                            >
                                Activas
                            </button>
                            <button
                                className={`vt-view-tab${viewMode === 'history' ? ' active' : ''}`}
                                onClick={() => setViewMode('history')}
                            >
                                Historial
                            </button>
                        </div>

                        {/* Summary banner */}
                        {!loading && (() => {
                            const now = new Date();
                            const todayCount = appointments.filter(a => {
                                const d = new Date(a.appointmentDate);
                                return d.getFullYear() === now.getFullYear() &&
                                    d.getMonth() === now.getMonth() &&
                                    d.getDate() === now.getDate() &&
                                    (a.status === APPOINTMENT_STATUS.SCHEDULED || a.status === APPOINTMENT_STATUS.IN_PROGRESS);
                            }).length;
                            return (
                                <div className="vt-summary">
                                    <div className="vt-summary-text">
                                        <div className="vt-summary-label">Hoy</div>
                                        <div className="vt-summary-value">
                                            {todayCount} cita{todayCount !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                    <div className="vt-summary-icon">🩺</div>
                                </div>
                            );
                        })()}

                        {/* Search */}
                        <div className="vt-search-wrap">
                            <span className="vt-search-icon">🔍</span>
                            <input
                                className="vt-search"
                                name="searchTerm"
                                value={filters.searchTerm}
                                onChange={handleFilterChange}
                                placeholder="Buscar por perro, propietario o motivo…"
                            />
                        </div>

                        {/* Status filter chips */}
                        <div className="vt-chips">
                            <button
                                className={`vt-chip${!filters.status ? ' on' : ''}`}
                                onClick={() => updateFilters({ status: '' })}
                            >Todas</button>
                            {Object.entries(STATUS_LABELS).map(([value, label]) => (
                                <button
                                    key={value}
                                    className={`vt-chip vt-chip-${value}${filters.status === value ? ' on' : ''}`}
                                    onClick={() => updateFilters({ status: value })}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        {loading ? (
                            <p className="vt-empty">Cargando…</p>
                        ) : (() => {
                            const isFiltered = filters.searchTerm || filters.status;

                            if (isFiltered) {
                                return appointments.length === 0 ? (
                                    <p className="vt-empty">Ninguna cita coincide con tu búsqueda.</p>
                                ) : (
                                    <div className="vt-appt-grid">
                                        {appointments.map(renderAppointmentCard)}
                                    </div>
                                );
                            }

                            // Filter out completed and cancelled appointments from active view
                            const activeAppointments = appointments.filter(a => 
                                a.status !== APPOINTMENT_STATUS.COMPLETED && 
                                a.status !== APPOINTMENT_STATUS.CANCELLED
                            );

                            // Also filter out appointments that are in the past and still scheduled
                            const now = new Date();
                            const filteredActiveAppointments = activeAppointments.filter(a => {
                                if (a.status === APPOINTMENT_STATUS.SCHEDULED || a.status === APPOINTMENT_STATUS.IN_PROGRESS) {
                                    return true;
                                }
                                return true;
                            });

                            // Time-based sections — active appointments only
                            const todayY = now.getFullYear(), todayM = now.getMonth(), todayD = now.getDate();

                            // Week: Monday–Sunday
                            const dow = now.getDay(); // 0=Sun
                            const mOff = dow === 0 ? -6 : 1 - dow;
                            const weekStart = new Date(todayY, todayM, todayD + mOff);
                            const weekEnd   = new Date(todayY, todayM, todayD + mOff + 7);

                            // Month boundaries
                            const monthStart     = new Date(todayY, todayM, 1);
                            const nextMonthStart = new Date(todayY, todayM + 1, 1);
                            const afterNextMonth = new Date(todayY, todayM + 2, 1);

                            const sortAsc  = arr => [...arr].sort((a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate));
                            const sortDesc = arr => [...arr].sort((a, b) => new Date(b.appointmentDate) - new Date(a.appointmentDate));

                            const thisWeek = sortAsc(filteredActiveAppointments.filter(a => {
                                const d = new Date(a.appointmentDate);
                                return d >= weekStart && d < weekEnd;
                            }));
                            const thisMonth = sortAsc(filteredActiveAppointments.filter(a => {
                                const d = new Date(a.appointmentDate);
                                return d >= monthStart && d < nextMonthStart && !(d >= weekStart && d < weekEnd);
                            }));
                            const nextMonth = sortAsc(filteredActiveAppointments.filter(a => {
                                const d = new Date(a.appointmentDate);
                                return d >= nextMonthStart && d < afterNextMonth;
                            }));
                            const later   = sortAsc(filteredActiveAppointments.filter(a => new Date(a.appointmentDate) >= afterNextMonth));
                            const earlier = sortDesc(filteredActiveAppointments.filter(a => new Date(a.appointmentDate) < monthStart));

                            const sections = [
                                { key: 'thisweek',  label: 'Esta semana',   appts: thisWeek },
                                { key: 'thismonth', label: 'Este mes',      appts: thisMonth },
                                { key: 'nextmonth', label: 'Mes siguiente', appts: nextMonth },
                                { key: 'later',     label: 'Más adelante', appts: later },
                                { key: 'earlier',   label: 'Anteriores',   appts: earlier },
                            ].filter(s => s.appts.length > 0);

                            return filteredActiveAppointments.length === 0 ? (
                                <p className="vt-empty">No hay citas activas.</p>
                            ) : (
                                sections.map(({ key, label, appts }) => {
                                    const isCollapsed = collapsedSections[key];
                                    return (
                                        <div key={key} className="vt-time-section">
                                            <div className="vt-section-row">
                                                <span className="vt-section-label">{label} · {appts.length}</span>
                                                {key === 'earlier' && (
                                                    <button
                                                        className="vt-section-toggle"
                                                        onClick={() => setCollapsedSections(prev => ({ ...prev, earlier: !prev.earlier }))}
                                                    >
                                                        {isCollapsed ? 'Mostrar' : 'Ocultar'}
                                                    </button>
                                                )}
                                            </div>
                                            {!isCollapsed && (
                                                <div className="vt-appt-grid">
                                                    {appts.map(renderAppointmentCard)}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            );
                        })()}
                    </div>
                )}

                {/* History Tab */}
                {activeTab === 'list' && viewMode === 'history' && (
                    <div className="vt-page">
                        {/* View mode tabs */}
                        <div className="vt-view-tabs">
                            <button
                                className={`vt-view-tab${viewMode === 'active' ? ' active' : ''}`}
                                onClick={() => setViewMode('active')}
                            >
                                Activas
                            </button>
                            <button
                                className={`vt-view-tab${viewMode === 'history' ? ' active' : ''}`}
                                onClick={() => setViewMode('history')}
                            >
                                Historial
                            </button>
                        </div>

                        {/* Search */}
                        <div className="vt-search-wrap">
                            <span className="vt-search-icon">🔍</span>
                            <input
                                className="vt-search"
                                name="searchTerm"
                                value={filters.searchTerm}
                                onChange={handleFilterChange}
                                placeholder="Buscar por perro, propietario o motivo…"
                            />
                        </div>

                        {loading ? (
                            <p className="vt-empty">Cargando…</p>
                        ) : (() => {
                            const historyAppointments = appointments.filter(a =>
                                a.status === APPOINTMENT_STATUS.COMPLETED ||
                                a.status === APPOINTMENT_STATUS.CANCELLED
                            );

                            if (historyAppointments.length === 0) {
                                return <p className="vt-empty">No hay citas en el historial.</p>;
                            }

                            const sortDesc = arr => [...arr].sort((a, b) => new Date(b.appointmentDate) - new Date(a.appointmentDate));
                            const sorted = sortDesc(historyAppointments);

                            // Group by "Month Year" label, most recent month first
                            const groups = [];
                            const groupMap = {};
                            sorted.forEach(a => {
                                const d = new Date(a.appointmentDate);
                                const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                                const label = d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
                                if (!groupMap[key]) {
                                    groupMap[key] = { key, label, appts: [] };
                                    groups.push(groupMap[key]);
                                }
                                groupMap[key].appts.push(a);
                            });

                            return groups.map(({ key, label, appts }) => (
                                <div key={key} className="vt-time-section">
                                    <div className="vt-section-row">
                                        <span className="vt-section-label" style={{ textTransform: 'capitalize' }}>
                                            {label} · {appts.length}
                                        </span>
                                    </div>
                                    <div className="vt-appt-grid">
                                        {appts.map(renderAppointmentCard)}
                                    </div>
                                </div>
                            ));
                        })()}
                    </div>
                )}

                {/* Booking Form Tab */}
                {activeTab === 'booking-form' && (
                    <VetBookingWizard
                        editingAppointment={editingAppointment}
                        onSubmit={handleBookingFormSubmit}
                        onCancel={() => { resetBookingForm(); setActiveTab('list'); }}
                        loading={loading}
                        appointments={appointments}
                    />
                )}

                {/* Walk-in Form Tab */}
                {activeTab === 'walkin-form' && (
                    <WalkInVetForm
                        editingAppointment={editingWalkIn}
                        onSubmit={handleWalkInSubmit}
                        onCancel={() => { setEditingWalkIn(null); setActiveTab('list'); }}
                        loading={loading}
                    />
                )}

                {/* Examination Form Tab */}
                {activeTab === 'exam-form' && conductingExam && (() => {
                    const examDogName = conductingExam.dog?.name
                        || conductingExam.walkInInfo?.dogName
                        || 'Perro desconocido';
                    return (
                    <div className="vt-exam-page">
                        {/* Dog bar */}
                        <div className="vt-exam-dog-bar">
                            <div className="vt-avatar" style={{ width: 54, height: 54, fontSize: 24 }}>
                                {conductingExam.dog?.pictureUrl || conductingExam.dog?.picture
                                    ? <img src={conductingExam.dog.pictureUrl || conductingExam.dog.picture} alt={conductingExam.dog.name} />
                                    : <span>{examDogName?.[0]?.toUpperCase() || '🐾'}</span>
                                }
                            </div>
                            <div className="vt-exam-dog-info">
                                <div className="vt-exam-dog-name">{examDogName}</div>
                                <div className="vt-exam-dog-meta">
                                    {[
                                        conductingExam.dog?.breed,
                                        conductingExam.dog?.weight && `${conductingExam.dog.weight}kg`,
                                        conductingExam.isWalkIn && conductingExam.walkInInfo?.fullName,
                                        conductingExam.cause,
                                    ].filter(Boolean).join(' · ')}
                                </div>
                            </div>
                            <span className="vt-pill-exam">En examen</span>
                        </div>

                        {/* Vitals grid */}
                        <div className="vt-step-label">Signos vitales</div>
                        <div className="vt-vitals">
                            <div className="vt-vital vt-vital-input">
                                <div className="vt-vital-label">Temperatura</div>
                                <div className="vt-vital-value-wrap">
                                    <input
                                        className="vt-vital-value"
                                        type="number"
                                        name="temperature"
                                        value={examFormData.temperature}
                                        onChange={handleExamInputChange}
                                        step="0.1" min="32" max="43"
                                        placeholder="38.5"
                                    />
                                    <span className="vt-vital-unit">°C</span>
                                </div>
                            </div>
                            <div className="vt-vital vt-vital-input">
                                <div className="vt-vital-label">Peso</div>
                                <div className="vt-vital-value-wrap">
                                    <input
                                        className="vt-vital-value"
                                        type="number"
                                        name="weight"
                                        value={examFormData.weight}
                                        onChange={handleExamInputChange}
                                        step="0.1" min="0" max="150"
                                        placeholder="25"
                                    />
                                    <span className="vt-vital-unit">kg</span>
                                </div>
                            </div>
                            <div className="vt-vital">
                                <div className="vt-vital-label">Corazón</div>
                                <div className="vt-vital-value-wrap">
                                    <input className="vt-vital-value" type="number" placeholder="—" min="0" max="300" />
                                    <span className="vt-vital-unit">bpm</span>
                                </div>
                            </div>
                            <div className="vt-vital">
                                <div className="vt-vital-label">Resp.</div>
                                <div className="vt-vital-value-wrap">
                                    <input className="vt-vital-value" type="number" placeholder="—" min="0" max="100" />
                                    <span className="vt-vital-unit">/min</span>
                                </div>
                            </div>
                        </div>

                        {/* Findings */}
                        <div className="vt-sched-section-hdr"><h2>Hallazgos</h2></div>
                        <div className="vt-exam-findings">
                            <textarea
                                className="vt-bw-textarea"
                                name="findings"
                                value={examFormData.findings}
                                onChange={handleExamInputChange}
                                rows={4}
                                placeholder="Condición del pelaje, resultados de palpación, observaciones…"
                            />
                            <div className="vt-exam-chips">
                                {['🦷 Dental', '🐾 Paws', '👁 Eyes', '👂 Ears', '🩺 Heart', '🫁 Lungs'].map(tag => (
                                    <button
                                        key={tag}
                                        type="button"
                                        className="vt-exam-chip"
                                        onClick={() => setExamFormData(prev => ({
                                            ...prev,
                                            findings: prev.findings
                                                ? `${prev.findings}\n${tag}: `
                                                : `${tag}: `,
                                        }))}
                                    >
                                        {tag}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Recommendations */}
                        <div className="vt-sched-section-hdr"><h2>Recomendaciones</h2></div>
                        <div className="vt-exam-rec">
                            <textarea
                                className="vt-bw-textarea"
                                name="recommendations"
                                value={examFormData.recommendations}
                                onChange={handleExamInputChange}
                                rows={3}
                                placeholder="Tratamientos, medicamentos, cuidados de seguimiento…"
                            />
                        </div>

                        {/* Vet notes */}
                        <div className="vt-sched-section-hdr"><h2>Notas del veterinario</h2></div>
                        <div className="vt-exam-rec">
                            <textarea
                                className="vt-bw-textarea"
                                name="vetNotes"
                                value={examFormData.vetNotes}
                                onChange={handleExamInputChange}
                                rows={2}
                                placeholder="Notas adicionales o recordatorios…"
                            />
                        </div>

                        {/* Attachments */}
                        <div className="vt-sched-section-hdr"><h2>Archivos adjuntos</h2></div>
                        <div className="vt-attach-section">
                            {/* Existing attachments */}
                            {attachments.length > 0 && (
                                <div className="vt-attach-grid">
                                    {attachments.map(att => {
                                        const isImage = att.mimeType?.startsWith('image/');
                                        return (
                                            <div key={att._id} className="vt-attach-item">
                                                {isImage ? (
                                                    <a href={att.url} target="_blank" rel="noopener noreferrer">
                                                        <img src={att.url} alt={att.originalName} className="vt-attach-thumb" />
                                                    </a>
                                                ) : (
                                                    <a href={att.url} target="_blank" rel="noopener noreferrer" className="vt-attach-file">
                                                        <span className="vt-attach-file-icon">📄</span>
                                                        <span className="vt-attach-file-name">{att.originalName || 'Archivo'}</span>
                                                    </a>
                                                )}
                                                <button
                                                    type="button"
                                                    className="vt-attach-delete"
                                                    onClick={() => handleDeleteAttachment(att._id)}
                                                    title="Eliminar"
                                                >✕</button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Upload button */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,application/pdf"
                                multiple
                                style={{ display: 'none' }}
                                onChange={e => handleAttachFiles(e.target.files)}
                            />
                            <button
                                type="button"
                                className="vt-attach-upload-btn"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={attachUploading}
                            >
                                {attachUploading ? 'Subiendo…' : '📎 Agregar foto o archivo'}
                            </button>
                        </div>

                        {/* Follow-up */}
                        <div className="vt-exam-followup">
                            <label className="vt-exam-followup-row">
                                <input
                                    type="checkbox"
                                    name="followUpRequired"
                                    checked={examFormData.followUpRequired}
                                    onChange={handleExamInputChange}
                                />
                                <span>Requiere cita de seguimiento</span>
                            </label>
                            {examFormData.followUpRequired && (
                                <input
                                    className="vt-bw-input"
                                    type="date"
                                    name="followUpDate"
                                    value={examFormData.followUpDate}
                                    onChange={handleExamInputChange}
                                    style={{ marginTop: 8 }}
                                />
                            )}
                        </div>

                        {/* Actions */}
                        <div className="vt-exam-actions">
                            <button
                                type="button"
                                className="vt-exam-btn-draft"
                                onClick={() => handleExamSubmit(APPOINTMENT_STATUS.IN_PROGRESS)}
                                disabled={loading}
                            >
                                Guardar borrador
                            </button>
                            <button
                                type="button"
                                className="vt-exam-btn-complete"
                                onClick={() => handleExamSubmit(APPOINTMENT_STATUS.COMPLETED)}
                                disabled={loading}
                            >
                                Completar examen
                            </button>
                        </div>
                    </div>
                    );
                })()}
            </div>
        </div>
    );
};

export default Vet;